/**
 * CoreQuestions - Three core foundation questions with auto-save on blur
 *
 * Key requirements:
 * - Auto-save on BLUR only (not on keystroke)
 * - IMMEDIATE save on blur (no debounce - blur is already infrequent)
 * - Character count visible only when > 200 chars
 * - Multi-line textareas with 3-row minimum
 *
 * Implementation: Standard controlled component with local state
 * - Initialize state from props
 * - Always sync state from props (props are source of truth)
 * - Only save changed values on blur
 */

import { useCallback, useEffect, useState } from 'react'
import { IntimacyExplanation } from './IntimacyExplanation'
import type { CoreQuestionsProps, FoundationQuestions } from './types'

interface QuestionField {
  key: keyof FoundationQuestions
  label: string
  placeholder: string
}

const QUESTION_FIELDS: QuestionField[] = [
  {
    key: 'who_is_talking',
    label: 'Who is Talking?',
    placeholder: 'e.g., A person struggling with emotional numbness',
  },
  {
    key: 'to_whom',
    label: 'To Whom?',
    placeholder: 'e.g., The audience, Their younger self, A specific person',
  },
  {
    key: 'why_message',
    label: 'Why (Core Message)?',
    placeholder:
      'What is the fundamental message or emotional truth you want to convey?',
  },
]

export function CoreQuestions({
  foundationQuestions,
  onUpdateQuestion
}: CoreQuestionsProps) {
  // Local state for all field values - initialized and synced from props
  const [values, setValues] = useState<FoundationQuestions>(() => ({
    who_is_talking: foundationQuestions.who_is_talking || '',
    to_whom: foundationQuestions.to_whom || '',
    why_message: foundationQuestions.why_message || '',
  }))

  // Sync from props when they change (e.g., modal reopens with saved data)
  // Only sync when props have actual data - don't reset local state from empty props
  // This prevents the bug where parent re-renders with {} before state propagates
  useEffect(() => {
    const hasPropsData =
      foundationQuestions.who_is_talking ||
      foundationQuestions.to_whom ||
      foundationQuestions.why_message

    // Only sync if props have data that differs from current state
    if (hasPropsData) {
      setValues(prev => {
        // Only update fields where props differ from current state
        const newValues = { ...prev }
        if (foundationQuestions.who_is_talking !== undefined) {
          newValues.who_is_talking = foundationQuestions.who_is_talking
        }
        if (foundationQuestions.to_whom !== undefined) {
          newValues.to_whom = foundationQuestions.to_whom
        }
        if (foundationQuestions.why_message !== undefined) {
          newValues.why_message = foundationQuestions.why_message
        }
        return newValues
      })
    }
  }, [foundationQuestions])

  // Handle text changes locally
  const handleChange = useCallback(
    (field: keyof FoundationQuestions, value: string) => {
      setValues(prev => ({
        ...prev,
        [field]: value,
      }))
    },
    []
  )

  // Save on blur - send to parent
  const handleBlur = useCallback(
    (field: keyof FoundationQuestions) => {
      const value = values[field]
      const trimmedValue = value?.trim() || undefined
      onUpdateQuestion(field, trimmedValue)
    },
    [values, onUpdateQuestion]
  )

  return (
    <div className="space-y-4" data-tour="core-questions">
      {/* Section header */}
      <h3 className="text-base font-semibold text-white">Core Foundation Questions</h3>

      {/* Intimacy explanation about foundation questions */}
      <IntimacyExplanation
        title="What are these questions for?"
        description="These foundational questions help clarify the core perspective and message of your song. They act as a compass for decision-making throughout the writing process—when you're unsure about a lyric or direction, these answers guide you back to the song's true voice and purpose."
        defaultExpanded={false}
      />

      {/* Question fields */}
      <div className="space-y-4">
        {QUESTION_FIELDS.map(({ key, label, placeholder }) => {
          const value = values[key] || ''
          const charCount = value.length
          const showCharCount = charCount > 200

          return (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {label}
              </label>
              <textarea
                data-testid={`core-question-${key.replace(/_/g, '-')}`}
                value={value}
                onChange={(e) => handleChange(key, e.target.value)}
                onBlur={() => handleBlur(key)}
                placeholder={placeholder}
                rows={3}
                className="w-full border border-white/20 rounded-md px-3 py-2 text-sm text-white bg-white/10 backdrop-blur-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-400 resize-y"
              />
              {showCharCount && (
                <div className="mt-1 text-xs text-gray-400 text-right">
                  {charCount} characters
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
